import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Calendar, FileText, Users, LogOut, Menu, X, CreditCard,
  LayoutDashboard, BarChart, Settings, Sparkles, Shield, GitBranch, UserCircle,
  Network, User, Palette, Megaphone, Globe, ChevronDown, AlertTriangle, Info, AlertCircle, Clock,
  GraduationCap, BookOpen, IndianRupee, MessageSquare, ClipboardList, CalendarCheck
} from 'lucide-react';
import { NotificationBell } from '../components/dashboard/NotificationBell';
import { useSessionTimeout } from '../hooks/useSessionTimeout';

const priorityStyles = {
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Info },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle },
  urgent: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: AlertCircle },
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

function LanguageDropdown() {
  const { lang, setLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative" data-testid="language-selector">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors text-sm text-slate-600" data-testid="language-btn">
        <Globe className="h-4 w-4 text-slate-400" />
        <span className="hidden sm:inline font-medium">{languages[lang]}</span>
        <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="language-dropdown">
            {Object.entries(languages).map(([code, label]) => (
              <button key={code} onClick={() => { setLang(code); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${lang === code ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid={`lang-${code}`}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useSessionTimeout(); // CERT-In: auto-logout on inactivity
  const [activeAnnouncements, setActiveAnnouncements] = useState([]);
  const [enabledModules, setEnabledModules] = useState(null);
  const [dismissedAnn, setDismissedAnn] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissed_ann') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    api.get('/school/announcements/active').then(res => {
      setActiveAnnouncements(res.data.data.announcements || []);
    }).catch(() => {});
    api.get('/school/modules').then(res => {
      setEnabledModules(res.data.data.modules || {});
    }).catch(() => {});
  }, [location.pathname]);

  const dismissAnn = (id) => {
    const updated = [...dismissedAnn, id];
    setDismissedAnn(updated);
    sessionStorage.setItem('dismissed_ann', JSON.stringify(updated));
  };

  const visibleAnn = activeAnnouncements.filter(a => !dismissedAnn.includes(a.announcement_id));

  const handleLogout = () => { logout(); navigate('/login'); };
  const isAdmin = user?.role === 'school_admin' || user?.role === 'hr';

  const isModuleOn = (key) => !enabledModules || enabledModules[key]?.enabled !== false;

  const navItems = [
    { path: '/dashboard', key: 'nav_dashboard', icon: LayoutDashboard, show: true },
    { path: '/dashboard/leave', key: 'nav_leave_dashboard', icon: Calendar, show: true, module: 'leave_management' },
    { path: '/dashboard/profile', key: 'nav_my_profile', icon: User, show: true },
    { path: '/dashboard/analytics', key: 'nav_analytics', icon: BarChart, show: isAdmin, module: 'analytics' },
    { path: '/dashboard/requests', key: 'nav_approvals', icon: FileText, show: isAdmin, module: 'leave_management' },
    { path: '/dashboard/employees', key: 'nav_staff_directory', icon: UserCircle, show: isAdmin },
    { path: '/dashboard/org-chart', key: 'nav_org_chart', icon: Network, show: true },
    { path: '/dashboard/team-calendar', key: 'nav_team_calendar', icon: Calendar, show: isAdmin },
    { path: '/dashboard/users', key: 'nav_staff_management', icon: Users, show: isAdmin },
    { path: '/dashboard/classes', key: 'Classes', icon: GraduationCap, show: isAdmin, module: 'classes' },
    { path: '/dashboard/students', key: 'Students', icon: BookOpen, show: isAdmin, module: 'students' },
    { path: '/dashboard/fees', key: 'Fee Management', icon: IndianRupee, show: isAdmin, module: 'fees' },
    { path: '/dashboard/tickets', key: 'Grievances', icon: MessageSquare, show: isAdmin, module: 'tickets' },
    { path: '/dashboard/exams', key: 'Exams', icon: ClipboardList, show: isAdmin, module: 'exams' },
    { path: '/dashboard/attendance', key: 'Attendance', icon: CalendarCheck, show: isAdmin, module: 'attendance' },
    { path: '/dashboard/leave-config', key: 'nav_leave_policies', icon: Settings, show: user?.role === 'school_admin', module: 'leave_management' },
    { path: '/dashboard/holidays', key: 'nav_holidays', icon: Calendar, show: isAdmin, module: 'holidays' },
    { path: '/dashboard/work-schedule', key: 'nav_work_schedule', icon: Clock, show: isAdmin, module: 'work_schedule' },
    { path: '/dashboard/workflows', key: 'nav_workflows', icon: GitBranch, show: user?.role === 'school_admin' },
    { path: '/dashboard/announcements', key: 'nav_announcements', icon: Megaphone, show: user?.role === 'school_admin', module: 'announcements' },
    { path: '/dashboard/branding', key: 'nav_school_branding', icon: Palette, show: user?.role === 'school_admin' },
    // { path: '/dashboard/billing', key: 'nav_billing', icon: CreditCard, show: isAdmin, module: 'billing' },
  ];

  return (
    <div className="dashboard-layout" data-testid="dashboard-layout">
      <ScrollToTop />

      {/* Mobile Menu Button */}
      <button className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-xl bg-white border border-slate-200 shadow-sm" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
        {sidebarOpen ? <X className="h-5 w-5 text-slate-700" /> : <Menu className="h-5 w-5 text-slate-700" />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'mobile-open' : ''}`} data-testid="sidebar">
        <div className="p-5 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-md shadow-teal-500/20">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-800 tracking-tight">Ariskool</span>
          </Link>
        </div>

        <nav className="p-3 space-y-0.5 mt-1 flex-1 overflow-y-auto" data-testid="sidebar-nav" style={{ maxHeight: 'calc(100vh - 80px)' }}>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-2">{t('navigation')}</p>
          {navItems.filter(item => item.show && (!item.module || isModuleOn(item.module))).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150 ${isActive ? 'bg-teal-50 text-teal-700 font-semibold border border-teal-100' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path}`}
              >
                <Icon className={`h-4 w-4 ${isActive ? 'text-teal-600' : 'text-slate-400'}`} />
                {t(item.key)}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Area */}
      <div className="flex-1" style={{ marginLeft: '260px' }}>
        {/* Top Header Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-200/60 px-6 py-3 flex items-center justify-between" data-testid="top-header">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full avatar-glossy flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
            </div>
            <div className="min-w-0 hidden sm:block">
              <p className="text-sm font-semibold text-slate-800 truncate" data-testid="user-name">{user?.full_name}</p>
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3 text-teal-500" />
                <p className="text-[10px] text-teal-600 font-medium capitalize" data-testid="user-role">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2" data-testid="header-actions">
            <LanguageDropdown />
            <NotificationBell />
            <Button variant="ghost" size="sm" className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline text-sm">{t('logout')}</span>
            </Button>
          </div>
        </header>

        {/* Announcement Banners */}
        {visibleAnn.length > 0 && (
          <div className="px-6 pt-3 space-y-2" data-testid="announcement-banners">
            {visibleAnn.slice(0, 3).map(a => {
              const ps = priorityStyles[a.priority] || priorityStyles.info;
              const Icon = ps.icon;
              return (
                <div key={a.announcement_id} className={`flex items-start gap-3 px-4 py-3 rounded-xl ${ps.bg} border ${ps.border}`} data-testid={`banner-${a.announcement_id}`}>
                  <Icon className={`h-4 w-4 ${ps.text} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${ps.text}`}>{a.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5">{a.message}</p>
                  </div>
                  <button onClick={() => dismissAnn(a.announcement_id)} className="text-slate-400 hover:text-slate-600 flex-shrink-0" data-testid={`dismiss-${a.announcement_id}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Main Content */}
        <main className="p-6" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={() => setSidebarOpen(false)} data-testid="mobile-overlay" />
      )}
    </div>
  );
};

export default DashboardLayout;
